import type { Content, Editor } from '@tiptap/core'
import type { EditorAdapter } from '../adapters/adapter'
import { getMarkRange } from '@tiptap/core'
import FileHandler from '@tiptap/extension-file-handler'
import Link from '@tiptap/extension-link'
import { TaskItem, TaskList } from '@tiptap/extension-list'
import Placeholder from '@tiptap/extension-placeholder'
import { TableKit } from '@tiptap/extension-table'
import Typography from '@tiptap/extension-typography'
import { Markdown } from '@tiptap/markdown'
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useMemo, useRef, useState } from 'react'
import { CodeBlockHighlight } from '../extensions/code-block-highlight'
import { ResizableImage } from '../extensions/image'

export interface LinkDialogState {
  url: string
  text: string
  isEditing: boolean
  from: number
  to: number
}

export interface WikiLinkDialogState {
  page: string
  anchor: string | null
  display: string | null
  project: string | null
  isEditing: boolean
  suggestionUrl: string
  range?: {
    from: number
    to: number
  }
}

interface Props {
  adapter: EditorAdapter
  initialValue: string
  onUpdate?: (value: string) => void
}

export function useEditorStore(props: Props) {
  const [ready, setReady] = useState(false)
  const { adapter, initialValue, onUpdate } = props

  /* ── Upload helpers ───────────────────────────────────────────── */

  async function uploadFiles(files: FileList | File[]) {
    await _handleFiles(Array.from(files))
  }

  async function _handleFiles(files: File[]) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const id = Date.now() + i
      const result = await _uploadFile(file, id)
      if (result) {
        _registerAttachment(id, result.token, file.name)
        _insertFileIntoEditor(file, result.attachmentId)
      }
    }
  }

  async function _uploadFile(file: File, id: number): Promise<{ token: string, attachmentId: string } | null> {
    const base = adapter.uploadPath
    const url = `${base}?attachment_id=${id}&filename=${encodeURIComponent(file.name)}&content_type=${encodeURIComponent(file.type || 'application/octet-stream')}`
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream', 'X-CSRF-Token': csrfToken },
        body: file,
      })
      if (!resp.ok)
        return null
      const text = await resp.text()
      const token = text.match(/\.find\('input\.token'\)\.val\('([^']+)'\)/)?.[1]
      const attachmentId = text.match(/href:\s*'\/attachments\/(\d+)/)?.[1]
      if (!token || !attachmentId)
        return null
      return { token, attachmentId }
    }
    catch {
      return null
    }
  }

  function _registerAttachment(id: number, token: string, filename: string) {
    const container = document.querySelector(adapter.attachmentsContainerSelector)
    if (!container)
      return
    const span = document.createElement('span')
    span.id = `attachments_${id}`
    const t = document.createElement('input')
    t.type = 'hidden'
    t.name = `attachments[${id}][token]`
    t.value = token
    const f = document.createElement('input')
    f.type = 'hidden'
    f.name = `attachments[${id}][filename]`
    f.value = filename
    span.append(t, f)
    container.appendChild(span)
  }

  /* ── Dialog subscription API ──────────────────────────────────── */

  // `useEditorStore` re-runs its whole body on every render (unlike the Solid version this
  // was ported from, whose setup function ran once) - these must survive across renders so
  // that a subscriber registered on an earlier render's `store` is still reachable from
  // whichever render's `store.openXDialog()` a caller happens to hold.
  const onOpenImageDialogRef = useRef<(() => void) | null>(null)
  const onOpenLinkDialogRef = useRef<((state: LinkDialogState) => void) | null>(null)
  const onOpenWikiLinkDialogRef = useRef<((state: WikiLinkDialogState) => void) | null>(null)

  function subscribeImageDialog(cb: () => void) {
    onOpenImageDialogRef.current = cb
  }
  function subscribeLinkDialog(cb: (state: LinkDialogState) => void) {
    onOpenLinkDialogRef.current = cb
  }
  function subscribeWikiLinkDialog(cb: (state: WikiLinkDialogState) => void) {
    onOpenWikiLinkDialogRef.current = cb
  }

  function openImageDialog() {
    onOpenImageDialogRef.current?.()
  }

  function openLinkDialog() {
    const ed = editor()
    const isEditing = ed.isActive('link')
    const url = isEditing ? (ed.getAttributes('link').href ?? '') : ''
    const { from, to, empty } = ed.state.selection
    const text = empty ? '' : ed.state.doc.textBetween(from, to)
    onOpenLinkDialogRef.current?.({ url, text, isEditing, from, to })
  }

  function openWikiLinkDialog() {
    const ed = editor()
    const isEditing = ed.isActive('wikiLink')
    const attrs = isEditing ? ed.getAttributes('wikiLink') : {}
    const { $from, from, to, empty } = ed.state.selection

    let display: string | null = null
    let range: { from: number, to: number } | undefined

    if (isEditing) {
      const markType = ed.state.schema.marks.wikiLink
      const markRange = markType ? getMarkRange($from, markType) : undefined
      if (markRange) {
        display = ed.state.doc.textBetween(markRange.from, markRange.to)
        range = markRange
      }
    }
    else if (!empty) {
      display = ed.state.doc.textBetween(from, to)
      range = { from, to }
    }

    onOpenWikiLinkDialogRef.current?.({
      page: (attrs.page as string) ?? '',
      anchor: (attrs.anchor as string | null) ?? null,
      display,
      project: (attrs.project as string | null) ?? null,
      isEditing,
      suggestionUrl: adapter.wikiPageSuggestionUrl ?? '',
      range,
    })
  }

  /* ── Editor setup ─────────────────────────────────────────────── */

  // `useEditor`'s deps ([]) only control when the *Editor instance* is (re)created - it
  // still diffs `options.extensions` by reference on every render and calls
  // `editor.setOptions()` (rebuilding every ProseMirror plugin, FileHandler's included)
  // whenever that array isn't the same reference as last time. Since this hook's body runs
  // fresh on every render, an inline array here would be a new reference every time and
  // extensions would get torn down and rebuilt constantly - fatal for anything spanning
  // multiple browser events, like a native drag-and-drop gesture. `useMemo` with an empty
  // dep array keeps a single stable array, matching the "create once" intent of `[]` above.
  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4] },
      link: false,
      dropcursor: {
        color: '#0969da',
        width: 2,
      },
    }),
    Markdown.configure({
      markedOptions: {
        breaks: true,
      },
    }),
    Link.configure({
      openOnClick: false,
      autolink: true,
      HTMLAttributes: { rel: 'noopener noreferrer nofollow' },
    }),
    ResizableImage.configure({
      allowBase64: false,
      resize: {
        enabled: true,
        alwaysPreserveAspectRatio: true,
      },
    }),
    TableKit.configure({
      table: { resizable: true },
    }),
    TaskList,
    TaskItem.configure({
      nested: true,
    }),
    Placeholder.configure({
      placeholder: 'Start writing...',
    }),
    Typography,
    CodeBlockHighlight,
    FileHandler.configure({
      onDrop: (_editor, files) => { void _handleFiles(files) },
      onPaste: (_editor, files) => { void _handleFiles(files) },
    }),
    ...adapter.extensions,
  ],
  // eslint-disable-next-line react/exhaustive-deps -- intentionally frozen to the first render's closures (see comment above); they read live state via refs where needed.
  [])

  const tiptap = useEditor({
    immediatelyRender: false,
    content: initialValue,
    contentType: 'markdown',
    extensions,
    onCreate() { setReady(true) },
    onUpdate({ editor: ed }) {
      onUpdate?.(ed.getMarkdown())
    },
  }, [])

  // `_insertFileIntoEditor` is reachable from `FileHandler`'s onDrop/onPaste, which are
  // frozen inside the `extensions` useMemo above (first render only) - `immediatelyRender:
  // false` means `tiptap` is still null on that very first render, so a plain closure over
  // `tiptap` here would stay stuck on that initial `null` forever. Route through a ref kept
  // in sync every render instead, so the frozen closure always sees the live editor.
  const editorRef = useRef<Editor | null>(null)
  editorRef.current = tiptap

  function _insertFileIntoEditor(file: File, attachmentId: string) {
    const ed = editorRef.current
    if (!ed)
      return
    if (file.type.startsWith('image/')) {
      const src = `/attachments/download/${attachmentId}/${file.name}`
      ed.chain().focus().setImage({ src }).run()
    }
    else {
      ed.chain().focus().insertContent(`attachment:${file.name}`).run()
    }
  }

  function editor(): Editor {
    if (!tiptap)
      throw new Error('Editor not initialized')
    return tiptap
  }

  function updateContent(content: Content) {
    editor().commands.setContent(content, { contentType: 'markdown' })
  }

  function destroy() {
    tiptap?.destroy()
  }

  return {
    editor,
    rawEditor: tiptap,
    ready,
    updateContent,
    destroy,
    adapter,
    uploadFiles,
    subscribeImageDialog,
    subscribeLinkDialog,
    subscribeWikiLinkDialog,
    openImageDialog,
    openLinkDialog,
    openWikiLinkDialog,
  }
}
